import type { Ref } from 'vue'
import type { InsertPreparationHandle } from './useEditorGlbLoader'
import {
  setPendingInsertionHandle,
  type PendingInsertionHandleRef,
} from './insertion-handle-state'

/**
 * 装配体引入取消工作流参数。
 *
 * Task 3 起，`discardPreparedInsertion` / `cancelInsertion` 在 loader 内部都通过
 * 当前 insertion request handle 的 `abort()` 走统一的取消链：
 *   - 句柄在途（已返回给 UI）→ `discardPreparedInsertion(handle)`：match current handle
 *     则 abort + dispose result。
 *   - 句柄还未返回（仍在 loading）→ `cancelInsertion()`：abort current handle 使 await
 *     回来的结果被判定 stale，loader 侧自动 dispose。
 * 两条分支都不再依赖 pendingInsertionSeq 之类 legacy 计数器语义。
 */
export interface CancelAssemblyInsertionWorkflowOptions {
  /** 当前是否处于预览放置模式。 */
  isPlacingComponent: Ref<boolean>
  /** 当前待提交的插入句柄。 */
  pendingInsertionHandle: PendingInsertionHandleRef
  /** 取消预览放置。 */
  cancelPlacement: () => void
  /** 丢弃已准备好的插入句柄（loader 内部通过 handle.abort() 剥夺 commit 资格）。 */
  discardPreparedInsertion: (handle: InsertPreparationHandle) => void
  /** 取消仍在途的 prepareInsertion（loader 内部通过当前 handle.abort() 触发）。 */
  cancelInsertion: () => void
  /** 结束放置流程并重置 UI 状态。 */
  finishPlacement: () => void
}

/**
 * 统一取消装配体引入工作流。
 *
 * 设计说明：
 * 1. PropertyManager、Esc、右键都必须走同一条释放链，避免句柄、预览对象和 UI 状态分裂；
 * 2. 若当前已进入预览放置模式，交给放置控制器触发 onCancel 完成资源释放；
 * 3. 若仍处于"已选文件但尚未进入预览"阶段，则直接丢弃句柄或失效在途请求，并清空 UI 状态。
 *    两条分支在 loader 内部都通过 request handle 的 `abort()` 收口，不再绕行 seq 失效。
 *
 * @param options 取消工作流依赖。
 */
export function cancelAssemblyInsertionWorkflow(
  options: CancelAssemblyInsertionWorkflowOptions,
): void {
  if (options.isPlacingComponent.value) {
    options.cancelPlacement()
    return
  }

  const handle = options.pendingInsertionHandle.value
  if (handle) {
    options.discardPreparedInsertion(handle)
  } else {
    options.cancelInsertion()
  }
  setPendingInsertionHandle(options.pendingInsertionHandle, null)
  options.finishPlacement()
}
